// Customize → Additional attributes: the setup screen (plan §7.4), built to
// the approved configuration prototype
// (docs/design/user-defined-attributes/configuration.html):
//
//   1 Form › 2 Decided by › 3 Combinations (Rules / Grid) › 4 Columns ›
//   5 Column properties (General / Data / Display / Behaviour / Rules),
//   plus Preview and Publish.
//
// The transaction designer's frame: a tree (form → decided by → combinations
// → sets), the work area for the selected step and a property panel. ONE
// component for the platform Run view, the downloaded runtime and the J2EE
// client; only the UdaTransport differs. Tenant mode edits the organization's
// draft (saved as you go, published on request); design mode edits the
// transaction designer's `transaction.uda` (plan §8, mounted in Phase 6).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronRight, Eye, History, SlidersHorizontal, X } from 'lucide-react'
import { tw } from '../../testHooks'
import type { KitNotifyKind } from '../../designerKit/catalog'
import type { UdaSetupScreenProps } from '../setupSlot'
import { udaDesignerCatalog } from './catalog'
import { driverLabel, formOf, formsOf, type LabelOf } from './model'
import { SetupContext, type ColTab, type SetupCtx, type SetupDialog, type SetupNode } from './state'
import { useUdaDraft } from './useUdaDraft'
import { useDomains } from './useDomains'
import { Banner, SetupStyles } from './ui'
import SetupTree from './SetupTree'
import StepForm, { FormPanel } from './StepForm'
import StepDecidedBy, { DeciderPanel } from './StepDecidedBy'
import StepCombinations from './StepCombinations'
import CombinationsPanel from './CombinationsPanel'
import StepColumns from './StepColumns'
import SetPanel from './SetPanel'
import ColumnProperties from './ColumnProperties'
import SetupDialogs from './SetupDialogs'

const STEP_OF: Record<SetupNode['t'], number> = { root: 1, decide: 2, combos: 3, set: 4, col: 5 }

export default function UdaSetupScreen(props: UdaSetupScreenProps) {
  const { transport, objName, onClose, model } = props
  const mode = props.mode === 'design' ? 'design' : 'tenant'
  const forms = useMemo(() => formsOf(model), [model])
  const draft = useUdaDraft({ mode, transport, objName, layer: props.layer, onChange: props.onChange })
  const domains = useDomains(transport, objName, forms)

  const [formNo, setFormNo] = useState<string>(() => props.formNo || forms[0]?.formNo || '1')
  const [node, setNode] = useState<SetupNode>({ t: 'root' })
  const [tab, setTab] = useState<ColTab>('general')
  const [dialog, setDialog] = useState<SetupDialog>(null)
  const [cview, setCview] = useState<'rules' | 'grid'>('rules')
  const [toast, setToast] = useState<{ kind: KitNotifyKind; text: string } | null>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const notify = useCallback((kind: KitNotifyKind, text: string) => {
    if (kind === 'error') console.error(`[uda setup] ${text}`)
    setToast({ kind, text })
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(null), kind === 'error' ? 6000 : 3000)
  }, [])
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current) }, [])

  const catalog = useMemo(() => udaDesignerCatalog(transport, objName, notify), [transport, objName, notify])

  // Open where the field panel pointed (a column of a set), once loaded.
  const opened = useRef(false)
  useEffect(() => {
    if (opened.current || draft.loading) return
    opened.current = true
    const want = String(props.columnKey || '').replace(/^uda_/, '')
    if (!want) return
    for (const [fno, f] of Object.entries(draft.layer.forms || {})) {
      if (props.formNo && fno !== props.formNo) continue
      for (const s of f.sets || []) {
        if ((s.columns || []).some(c => c.name === want)) {
          setFormNo(fno)
          setNode({ t: 'col', set: s.key, col: want })
          setTab('general')
          return
        }
      }
    }
  }, [draft.loading, draft.layer, props.columnKey, props.formNo])

  // Load the deciding values of the form in view.
  const form = formOf(draft.layer, formNo)
  useEffect(() => {
    if (form?.drivers?.length) domains.ensure(formNo, form.drivers)
  }, [formNo, form?.drivers, domains.ensure]) // eslint-disable-line react-hooks/exhaustive-deps

  const labelOf = useCallback((fno: string): LabelOf => {
    const f = formOf(draft.layer, fno)
    return (key: string, code: string) => {
      const d = f?.drivers.find(x => x.key === key)
      const v = d ? domains.domainOf(fno, d).values.find(x => x[0] === code) : null
      return v ? v[1] : code
    }
  }, [draft.layer, domains])

  const additionsAllowed = useCallback((fno: string) => {
    if (mode === 'design') return true
    const df = draft.design?.forms?.[fno]
    if (!df) return draft.design?.allow_tenant_additions !== false
    return df.allow_tenant_additions !== false && draft.design?.allow_tenant_additions !== false
  }, [mode, draft.design])

  const go = useCallback((n: SetupNode) => {
    setNode(n)
    if (n.t === 'col') setTab(t => t || 'general')
  }, [])

  const close = useCallback(async () => {
    if (mode === 'tenant' && draft.pending > 0) {
      const ok = await draft.flush()
      if (!ok) {
        notify('error', 'Your last change was not saved. Fix or discard it before leaving.')
        return
      }
    }
    onClose()
  }, [mode, draft, notify, onClose])

  // Design mode hands a standard attribute's function to the designer's own
  // business-logic editor (props.onOpenDesignerCode); everything else opens here.
  const onOpenDesignerCode = props.onOpenDesignerCode
  const openDialog = useCallback((d: SetupDialog) => {
    if (d && d.kind === 'code' && mode === 'design' && onOpenDesignerCode) {
      onOpenDesignerCode(d.fnName)
      return
    }
    setDialog(d)
  }, [mode, onOpenDesignerCode])

  const ctx: SetupCtx = {
    mode, draft, transport, objName, forms, formNo, setFormNo, node, go, tab, setTab, domains,
    labelOf, catalog, notify, dialog, openDialog, cview, setCview, additionsAllowed,
    project: props.project || '',
  }

  const setup = forms.find(f => f.formNo === formNo) || null
  const step = STEP_OF[node.t]
  const nCols = (form?.sets || []).reduce((n, s) => n + (s.columns || []).length, 0)
  const steps: Array<[number, string, string, SetupNode]> = [
    [1, 'Form', setup?.title || `Form ${formNo}`, { t: 'root' }],
    [2, 'Decided by', (form?.drivers || []).map(driverLabel).join(' + ') || 'Not set', { t: 'decide' }],
    [3, 'Combinations', `${(form?.sets || []).length} attribute sets`, { t: 'combos' }],
    [4, 'Columns', `${nCols} columns`, { t: 'set', set: '' }],
    [5, 'Column properties', 'Default, item change, validation', { t: 'col', set: '', col: '' }],
  ]
  const goStep = (n: number, target: SetupNode) => {
    if (target.t === 'set' || target.t === 'col') {
      const sets = form?.sets || []
      if (!sets.length) { notify('info', 'Add a combination first.'); return }
      const cur = node.t === 'set' || node.t === 'col' ? node.set : ''
      const s = sets.find(x => x.key === cur) || sets[0]
      if (target.t === 'col') {
        const cname = node.t === 'col' && node.set === s.key ? node.col : s.columns?.[0]?.name
        if (!cname) { go({ t: 'set', set: s.key }); return }
        go({ t: 'col', set: s.key, col: cname })
      } else go({ t: 'set', set: s.key })
      return
    }
    void n
    go(target)
  }

  const published = draft.publishedVersion
  const changes = draft.changes.length
  let stateText: string
  let stateLive = false
  if (mode === 'design') { stateText = 'Saved with the transaction'; stateLive = true }
  else if (draft.saving) stateText = 'Saving…'
  else if (draft.pending) stateText = 'Unsaved change…'
  else if (changes) stateText = `Draft · ${changes} change${changes === 1 ? '' : 's'} not published`
  else if (published) { stateText = `Version ${published} published`; stateLive = true }
  else stateText = 'Nothing published yet'

  let work: React.ReactNode
  let panel: React.ReactNode
  if (!model || !forms.length) {
    work = (
      <Banner tone="e">
        This screen needs the transaction&apos;s forms, and none were given. Open it from the
        screen&apos;s Customize mode.
      </Banner>
    )
    panel = null
  } else if (node.t === 'decide') { work = <StepDecidedBy />; panel = <DeciderPanel /> }
  else if (node.t === 'combos') { work = <StepCombinations />; panel = <CombinationsPanel /> }
  else if (node.t === 'set') { work = <StepColumns />; panel = <SetPanel /> }
  else if (node.t === 'col') { work = <StepColumns />; panel = <ColumnProperties /> }
  else { work = <StepForm />; panel = <FormPanel /> }

  return (
    <SetupContext.Provider value={ctx}>
      <SetupStyles />
      <div className="uds-root" style={{ minHeight: 560 }} {...tw('screen', 'uda-setup', { mode })}>
        <header className="uds-top">
          <h1>
            <span className="uds-pill uds-p-cz" aria-hidden><SlidersHorizontal size={13} /></span>
            Additional attributes
          </h1>
          <span className="uds-crumbs">
            {forms[0]?.title || objName} › <b>{mode === 'design' ? 'Transaction designer' : 'Customize'}</b>
          </span>
          <span className="uds-grow" />
          <span className={`uds-state ${stateLive ? 'live' : ''}`} aria-live="polite">
            {draft.saving ? <span className="uds-spin" /> : <span className="dot" />}{stateText}
          </span>
          {mode === 'tenant' && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDialog({ kind: 'history' })}
              {...tw('action', 'uda-history')}>
              <History size={14} /> History
            </button>
          )}
          <button type="button" className="btn btn-secondary btn-sm" disabled={!forms.length}
            onClick={() => setDialog({ kind: 'preview' })} {...tw('action', 'uda-preview')}>
            <Eye size={14} /> Preview
          </button>
          {mode === 'tenant' && (
            <button type="button" className="btn btn-primary btn-sm" disabled={draft.loading || (!changes && !draft.pending)}
              onClick={() => setDialog({ kind: 'publish' })} {...tw('action', 'uda-publish')}>
              Publish
            </button>
          )}
          {/* Design mode lives inside the designer's Settings: nothing to go back to. */}
          {mode === 'tenant' && (
            <button type="button" className="uds-icbtn" aria-label="Close additional attributes" title="Back to Customize"
              onClick={() => { void close() }} {...tw('action', 'uda-close')}>
              <X size={16} />
            </button>
          )}
        </header>
        <nav className="uds-steps" aria-label="Steps">
          <div className="uds-steps-in">
            {steps.map(([n, title, sub, target], k) => (
              <span key={n} style={{ display: 'contents' }}>
                {k > 0 && <span className="uds-sep"><ChevronRight size={15} /></span>}
                <button type="button" className={`uds-step ${step === n ? 'on' : ''} ${step > n ? 'done' : ''}`}
                  onClick={() => goStep(n, target)} {...tw('action', `uda-step:${n}`)}
                  aria-current={step === n ? 'step' : undefined}>
                  <span className="n">{step > n ? '✓' : n}</span>{title}<span className="c">{sub}</span>
                </button>
              </span>
            ))}
          </div>
        </nav>
        {(draft.error || draft.notice || (mode === 'tenant' && draft.problems.length > 0)) && (
          <div style={{ padding: '10px 16px 0', display: 'grid', gap: 8 }}>
            {draft.error && (
              <Banner tone={draft.error.kind === 'refused' ? 'w' : 'e'} action={(
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  {draft.error.kind === 'load'
                    ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => { void draft.reload() }}>Try again</button>
                    : (
                      <>
                        {draft.error.kind === 'failed' && <button type="button" className="btn btn-secondary btn-sm" onClick={() => { void draft.flush() }}>Retry</button>}
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => { void draft.discard() }}>Discard unsaved changes</button>
                      </>
                    )}
                </span>
              )}>
                <b>{draft.error.kind === 'refused' ? 'This change was not accepted.' : draft.error.message}</b>
                {draft.error.kind === 'refused' && <div>{draft.error.message}</div>}
                {!!draft.error.errors?.length && <ul>{draft.error.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
              </Banner>
            )}
            {draft.notice && (
              <Banner tone="i" action={<button type="button" className="btn btn-secondary btn-sm" onClick={draft.clearNotice}>OK</button>}>
                {draft.notice}
              </Banner>
            )}
            {mode === 'tenant' && draft.problems.length > 0 && !draft.error && (
              <Banner tone="w">
                <b>Saved, with {draft.problems.length === 1 ? 'a warning' : `${draft.problems.length} warnings`}:</b>
                <ul>{draft.problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
              </Banner>
            )}
          </div>
        )}
        {draft.loading ? (
          <div style={{ padding: 24, display: 'flex', gap: 10, alignItems: 'center', color: 'var(--color-text-secondary)' }}>
            <span className="uds-spin" /> Reading the attribute settings…
          </div>
        ) : (
          <div className="uds-ws">
            <nav className="uds-tree" aria-label="Structure"><SetupTree /></nav>
            <main className="uds-work">{work}</main>
            <aside className="uds-props" aria-label="Properties">{panel}</aside>
          </div>
        )}
        <SetupDialogs codeEditor={props.codeEditor} />
        {toast && (
          <div className={`uds-toast ${toast.kind === 'error' ? 'error' : ''} tw-msg tw-msg-${toast.kind}`} role="status"
            {...tw('msg', toast.kind)}>{toast.text}</div>
        )}
      </div>
    </SetupContext.Provider>
  )
}

